import { buildQuery, ensureCsrfToken, resetCsrfToken, API_BASE_URL } from "./apiClient";
import { normalizePartnerWhatsappSummary } from "../utils/partnerWhatsappSummaryViewModel";
import { normalizePartnerWhatsappConversations } from "../utils/partnerWhatsappConversationsViewModel";
import {
  normalizePartnerWhatsappConversationDetail,
  normalizePartnerWhatsappTimeline,
} from "../utils/partnerWhatsappConversationDetailViewModel";
import { normalizePartnerWhatsappDealerHealth } from "../utils/partnerWhatsappDealerHealthViewModel";

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const CSRF_EXEMPT_PATHS = new Set([
  "/api/partner/auth/login",
  "/api/partner/auth/logout",
  "/api/partner/auth/request-password-reset",
  "/api/partner/auth/reset-password",
]);

function normalizePath(path) {
  const withoutQuery = String(path || "").split("?")[0];
  if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery;
}

function needsCsrfToken(method, path) {
  if (!MUTATING_METHODS.has(method)) {
    return false;
  }
  return !CSRF_EXEMPT_PATHS.has(normalizePath(path));
}

function isCsrfErrorMessage(message) {
  return typeof message === "string" && message.toLowerCase().includes("csrf");
}

async function partnerPortalFetch(path, options = {}) {
  const { _csrfRetried, ...fetchOptions } = options;
  const method = (fetchOptions.method || "GET").toUpperCase();
  const headers = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers || {}),
  };

  if (needsCsrfToken(method, path)) {
    headers["X-CSRF-Token"] = await ensureCsrfToken();
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...fetchOptions,
    method,
    credentials: "include",
    headers,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = body.message || "Request failed";
    if (response.status === 403 && isCsrfErrorMessage(message) && !_csrfRetried) {
      resetCsrfToken();
      return partnerPortalFetch(path, { ...options, _csrfRetried: true });
    }

    const error = new Error(message);
    error.blockers = body.blockers;
    error.warnings = body.warnings;
    throw error;
  }
  return body;
}

export function getPartnerPortalDashboard() {
  return partnerPortalFetch("/api/partner/dashboard");
}

export function getPartnerInventoryHealth(params = {}) {
  const query = buildQuery(params);
  return partnerPortalFetch(`/api/partner/inventory-health${query}`);
}

async function partnerPortalFetchFirst(paths, options = {}) {
  let lastError = null;

  for (const path of paths) {
    try {
      return await partnerPortalFetch(path, options);
    } catch (error) {
      lastError = error;
      if (!/route not found/i.test(String(error.message || ""))) {
        throw error;
      }
    }
  }

  throw lastError || new Error("Request failed");
}

function dataSourceListPaths() {
  return [
    "/api/partner/inventory-health/data-sources",
    "/api/partner/automap/inventory-health/data-sources",
  ];
}

function dataSourceActionPaths(sourceCode, action) {
  const encoded = encodeURIComponent(sourceCode);
  const suffix = `/inventory-health/data-sources/${encoded}/${action}`;
  return [`/api/partner${suffix}`, `/api/partner/automap${suffix}`];
}

export async function listPartnerInventoryDataSources() {
  try {
    return await partnerPortalFetchFirst(dataSourceListPaths());
  } catch (error) {
    if (!/route not found/i.test(String(error.message || ""))) {
      throw error;
    }

    const result = await getPartnerInventoryHealth({ view: "data-sources" });
    const sources = result.dataSources || result.sources || [];
    return {
      sources,
      count: result.count ?? sources.length,
    };
  }
}

export function runPartnerInventoryDataSource(sourceCode) {
  return partnerPortalFetchFirst(dataSourceActionPaths(sourceCode, "run"), {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function enablePartnerInventoryDataSourceSchedule(sourceCode, scheduleIntervalMinutes) {
  return partnerPortalFetchFirst(dataSourceActionPaths(sourceCode, "schedule/enable"), {
    method: "POST",
    body: JSON.stringify({ scheduleIntervalMinutes }),
  });
}

export function disablePartnerInventoryDataSourceSchedule(sourceCode) {
  return partnerPortalFetchFirst(dataSourceActionPaths(sourceCode, "schedule/disable"), {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function getPartnerProfile() {
  return partnerPortalFetch("/api/partner/profile");
}

export function getPartnerProfileWorkspace() {
  return partnerPortalFetch("/api/partner/profile/workspace");
}

export function updatePartnerProfile(payload) {
  return partnerPortalFetch("/api/partner/profile", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function uploadPartnerProfilePhoto(payload) {
  return partnerPortalFetch("/api/partner/profile/photo", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function clearPartnerProfilePhoto() {
  return partnerPortalFetch("/api/partner/profile/photo", {
    method: "DELETE",
  });
}

export function getPartnerAccount() {
  return partnerPortalFetch("/api/partner/account");
}

export function updatePartnerAccount(payload) {
  return partnerPortalFetch("/api/partner/account", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function listPartnerLeads(params = {}) {
  return partnerPortalFetch(`/api/partner/leads${buildQuery(params)}`);
}

export function getPartnerLead(assignmentId) {
  return partnerPortalFetch(`/api/partner/leads/${assignmentId}`);
}

export function updatePartnerLeadStatus(assignmentId, payload) {
  return partnerPortalFetch(`/api/partner/leads/${assignmentId}/status`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function addPartnerLeadNote(assignmentId, noteText) {
  return partnerPortalFetch(`/api/partner/leads/${assignmentId}/notes`, {
    method: "POST",
    body: JSON.stringify({ noteText }),
  });
}

export function listPartnerResponseTemplates() {
  return partnerPortalFetch("/api/partner/response-templates");
}

export function createPartnerResponseTemplate(payload) {
  return partnerPortalFetch("/api/partner/response-templates", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function deletePartnerResponseTemplate(templateId) {
  return partnerPortalFetch(`/api/partner/response-templates/${encodeURIComponent(templateId)}`, {
    method: "DELETE",
  });
}

export function listPartnerListingCorrections() {
  return partnerPortalFetch("/api/partner/listing-corrections");
}

export function createPartnerListingCorrection(payload) {
  return partnerPortalFetch("/api/partner/listing-corrections", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listPartnerSponsorshipAssets() {
  return partnerPortalFetch("/api/partner/sponsorship-assets");
}

export function createPartnerSponsorshipAsset(payload) {
  return partnerPortalFetch("/api/partner/sponsorship-assets", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function submitPartnerSponsorshipAsset(assetId) {
  return partnerPortalFetch(`/api/partner/sponsorship-assets/${assetId}/submit`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function updatePartnerSponsorshipAsset(assetId, payload) {
  return partnerPortalFetch(`/api/partner/sponsorship-assets/${encodeURIComponent(assetId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deletePartnerSponsorshipAsset(assetId) {
  return partnerPortalFetch(`/api/partner/sponsorship-assets/${encodeURIComponent(assetId)}`, {
    method: "DELETE",
  });
}

export function listPartnerSponsorshipPlacements() {
  return partnerPortalFetch("/api/partner/sponsorship/placements");
}

export function getPartnerSponsorshipPlacement(placementId) {
  return partnerPortalFetch(`/api/partner/sponsorship/placements/${encodeURIComponent(placementId)}`);
}

export function listPartnerCampaigns() {
  return partnerPortalFetch("/api/partner/campaigns");
}

export function getPartnerCampaign(campaignId) {
  return partnerPortalFetch(`/api/partner/campaigns/${encodeURIComponent(campaignId)}`);
}

export function listPartnerInvoices() {
  return partnerPortalFetch("/api/partner/invoices");
}

export async function getPartnerWhatsappSummary(params = {}, meta = {}) {
  const range = params.range === "30d" ? "30d" : "7d";
  const query = buildQuery({ range });
  const body = await partnerPortalFetch(`/api/partner/automap/whatsapp/summary${query}`);
  return normalizePartnerWhatsappSummary(body, meta);
}

export async function getPartnerWhatsappConversations(params = {}) {
  const query = buildQuery(params);
  const body = await partnerPortalFetch(`/api/partner/automap/whatsapp/conversations${query}`);
  return normalizePartnerWhatsappConversations(body);
}

export async function getPartnerWhatsappConversation(crmLeadId) {
  const body = await partnerPortalFetch(
    `/api/partner/automap/whatsapp/conversations/${encodeURIComponent(crmLeadId)}`
  );
  return normalizePartnerWhatsappConversationDetail(body);
}

export async function getPartnerWhatsappTimeline(crmLeadId) {
  const body = await partnerPortalFetch(
    `/api/partner/automap/whatsapp/conversations/${encodeURIComponent(crmLeadId)}/timeline`
  );
  return normalizePartnerWhatsappTimeline(body);
}

export async function getPartnerWhatsappDealerHealth(params = {}) {
  const query = buildQuery(params);
  const body = await partnerPortalFetch(`/api/partner/automap/whatsapp/dealers/health${query}`);
  return normalizePartnerWhatsappDealerHealth(body);
}

/** Midpoint Media customer portal (tickets / infrastructure / projects) */
export function getMidpointMediaCustomerOverview() {
  return partnerPortalFetch("/api/partner/midpoint-media/overview");
}

export function listMidpointMediaCustomerTickets() {
  return partnerPortalFetch("/api/partner/midpoint-media/tickets");
}

export function createMidpointMediaCustomerTicket(payload) {
  return partnerPortalFetch("/api/partner/midpoint-media/tickets", {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function listMidpointMediaCustomerProjects() {
  return partnerPortalFetch("/api/partner/midpoint-media/projects");
}

export function getMidpointMediaInfrastructureStatus() {
  return partnerPortalFetch("/api/partner/midpoint-media/infrastructure");
}
